import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useUID } from 'react-uid';
import { Business } from '../../backend/db/all-entities';
import { BusinessCategory, businessCategoryIsAllowed, CATEGORIES, Category } from '../../misc-types/category';

interface SingleProps {
  multiple?: false;
  radioInputName: string;
  selectedCategory: string | undefined;
  setSelectedCategory: Dispatch<SetStateAction<string | undefined>>;
}

interface MultipleProps {
  multiple: true;
  selectedCategories: string[];
  setSelectedCategories: Dispatch<SetStateAction<string[]>>;
}

type Props = {
  required?: boolean;
  categoryType?: Category['type'];
  businessType?: Business['type'];
  onlyShowCategoriesWhichRequireQualification?: boolean;
} & (SingleProps | MultipleProps);

const CategoryChooser: React.FC<Props> = ({ categoryType, businessType, ...props }) => {
  const { multiple } = props;
  const inputIdPrefix = useUID();
  // eslint-disable-next-line react/destructuring-assignment
  const setSelectedCategories = props.multiple ? props.setSelectedCategories : undefined;
  const businessCategorySlugIsAllowed = useCallback(
    (slug: string) =>
      businessType == null
        ? true
        : businessCategoryIsAllowed(CATEGORIES.find(c => c.slug === slug) as BusinessCategory, businessType),
    [businessType]
  );
  useEffect(() => {
    if (categoryType === 'business' && businessType != null) {
      if (multiple) {
        setSelectedCategories?.(categories => categories.filter(businessCategorySlugIsAllowed));
      }
    }
  }, [businessType, categoryType, multiple, businessCategorySlugIsAllowed, setSelectedCategories]);

  return (
    <div className='flex flex-wrap -mx-2 -my-1'>
      {CATEGORIES.filter(category => (categoryType ? category.type === categoryType : true))
        .filter(category => !props.onlyShowCategoriesWhichRequireQualification || category.requiresQualification)
        .map(category => [`${inputIdPrefix}-category-${category.slug}`, category] as const)
        .map(([inputId, category]) => (
          <div key={category.slug} className='leading-none mx-2 my-1 whitespace-no-wrap'>
            <input
              type={props.multiple ? 'checkbox' : 'radio'}
              checked={
                props.multiple
                  ? props.selectedCategories.includes(category.slug)
                  : (props as SingleProps).selectedCategory === category.slug
              }
              id={inputId}
              name={props.multiple ? undefined : inputIdPrefix}
              value={props.multiple ? undefined : category.slug}
              required={!props.multiple ? props.required : props.selectedCategories.length === 0}
              disabled={!businessCategorySlugIsAllowed(category.slug)}
              onChange={e => {
                if (props.multiple) {
                  if (e.target.checked) {
                    if (businessCategorySlugIsAllowed(category.slug)) {
                      props.setSelectedCategories(categories => [...categories, category.slug]);
                    }
                  } else {
                    props.setSelectedCategories(categories => categories.filter(c => c !== category.slug));
                  }
                } else if (businessCategorySlugIsAllowed(e.target.value)) {
                  props.setSelectedCategory(e.target.value);
                }
              }}
            />
            <label htmlFor={inputId}>{category.name}</label>
          </div>
        ))}
    </div>
  );
};

export default CategoryChooser;
